'use client';

import { ShieldAlert, Fingerprint, Calendar, ArrowRight } from 'lucide-react';
import Link from 'next/link';
import FadeIn from '../ui/FadeIn';
import { useTranslations } from '@/lib/i18n/TranslationProvider';

export default function PrivacyBriefing() {
    const { t } = useTranslations();

    const icons = [Fingerprint, ShieldAlert, Calendar];

    return (
        <section id="privacy-briefing" className="py-24 bg-[var(--color-md-surface-container-lowest)] relative overflow-hidden">
            {/* Ambient Background Elements */}
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-[var(--color-md-primary)]/5 blur-[120px] rounded-full pointer-events-none" />
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[var(--color-md-tertiary)]/5 blur-[120px] rounded-full pointer-events-none" />

            <div className="container mx-auto px-6 relative z-10">
                <div className="max-w-4xl mx-auto text-center mb-16">
                    <FadeIn direction="up">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--color-md-error-container)]/30 border border-[var(--color-md-error)]/20 text-[var(--color-md-error)] text-[10px] font-black uppercase tracking-[0.2em] mb-6">
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--color-md-error)] opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-[var(--color-md-error)]"></span>
                            </span>
                            {t.privacyBriefing.badge}
                        </div>
                        <h2 className="text-4xl md:text-5xl font-black text-[var(--color-md-on-surface)] tracking-tight mb-6 leading-tight">
                            {t.privacyBriefing.title} <span className="text-[var(--color-md-primary)]">{t.privacyBriefing.titleAccent}</span>
                        </h2>
                        <p className="text-lg text-[var(--color-md-on-surface-variant)] leading-relaxed font-medium">
                            {t.privacyBriefing.subtitle}
                        </p>
                    </FadeIn>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                    {t.privacyBriefing.cards.map((card, i) => {
                        const Icon = icons[i];
                        return (
                            <FadeIn key={i} delay={i * 0.1} direction="up" yOffset={20}>
                                <div className="group relative bg-[var(--color-md-surface)] rounded-[32px] p-8 border border-[var(--color-md-outline-variant)] hover:border-[var(--color-md-primary)]/30 transition-all duration-500 hover:shadow-cinematic-2 flex flex-col h-full">
                                    <div className="flex items-center justify-between mb-8">
                                        <div className={`w-12 h-12 flex-center rounded-2xl ${
                                            i === 0 ? 'm3-icon-tonal' : 
                                            i === 1 ? 'm3-icon-secondary' : 
                                            'm3-icon-tertiary'
                                        }`}>
                                            <Icon className="w-6 h-6" />
                                        </div>
                                        <span className="text-[10px] font-black uppercase tracking-widest text-[var(--color-md-outline)] opacity-60">
                                            {card.tag}
                                        </span>
                                    </div>
                                    <h3 className="text-xl font-bold text-[var(--color-md-on-surface)] mb-4 tracking-tight">
                                        {card.title}
                                    </h3>
                                    <p className="text-sm text-[var(--color-md-on-surface-variant)] leading-relaxed font-medium mb-6 flex-grow">
                                        {card.desc}
                                    </p>
                                    <div className="w-full h-px bg-gradient-to-r from-transparent via-[var(--color-md-outline-variant)] to-transparent opacity-30 mt-auto" />
                                </div>
                            </FadeIn>
                        );
                    })}
                </div>

                <div className="flex justify-center">
                    <FadeIn delay={0.4}>
                        <Link 
                            href="/guides/whatsapp-privacy-update-2026-strategy"
                            className="group flex items-center gap-3 px-8 py-4 rounded-full bg-[var(--color-md-primary)] text-white font-black uppercase tracking-wider text-sm shadow-cinematic-1 hover:shadow-cinematic-2 hover:scale-[1.02] active:scale-[0.98] transition-all"
                        >
                            {t.privacyBriefing.cta}
                            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                        </Link>
                    </FadeIn>
                </div>
            </div>
        </section>
    );
}
